import { Modules } from '@enum/central/modules.enum';

/** Roteiro de implantação: ações reais, sem avaliação de respostas. */
export interface CapituloOnboarding {
  id: string;
  titulo: string;
  subtitulo: string;
  ganho: string;
  contexto: string[];
  orientacao?: boolean;
  conceitos: { titulo: string; corpo: string }[];
  acoes: { rotulo: string; rota: string; criterio?: string; modulo?: Modules; rotuloConclusao?: string }[];
}

export function normalizarCapituloOnboarding(id: string | null | undefined): string {
  if (id === 'tabelas' || id === 'dashboard') return 'relatorios';
  return id ?? 'fundamentos';
}

export const CATALOGO_ONBOARDING: CapituloOnboarding[] = [
  {
    "id": "fundamentos",
    "orientacao": true,
    "titulo": "Conheça seu painel financeiro",
    "subtitulo": "Veja o que você poderá acompanhar com suas primeiras contas.",
    "ganho": "Enxergar vencimentos, entradas, saídas e a previsão de caixa em um só lugar.",
    "contexto": [
      "Conforme você registra as contas e as baixas, o dashboard ajuda a acompanhar o que vence, quanto entrou e saiu e como fica o caixa.",
      "Conheça essa visão primeiro. Depois, vamos cadastrar um cliente, um fornecedor e um funcionário, preparar o plano de contas, as categorias e os caixas e lançar as primeiras contas a pagar e a receber."
    ],
    "conceitos": [],
    "acoes": [
      {
        "rotulo": "Conhecer o dashboard passo a passo",
        "rota": "/painel/dashboard"
      }
    ]
  },
  {
    "id": "clientes",
    "titulo": "Prepare o cadastro do seu cliente",
    "subtitulo": "Identificação, contatos e endereço no mesmo cadastro.",
    "ganho": "Preparar os dados que serão reutilizados nas contas e documentos.",
    "contexto": [
      "Separe os dados cadastrais e o contato do financeiro do cliente. Pesquise o nome ou documento na listagem para evitar duplicidade antes de criar.",
      "O cadastro será reutilizado nas contas a receber, cobranças e documentos. Um contato de cobrança incorreto pode levar a comunicação para a pessoa errada."
    ],
    "conceitos": [
      {
        "titulo": "Endereço e identificação",
        "corpo": "Nome e documento identificam o cliente; o endereço apoia a emissão de documentos. Confira os dados antes de faturar ou emitir uma nota."
      },
      {
        "titulo": "E-mail e WhatsApp",
        "corpo": "O e-mail e o telefone permitem enviar e acompanhar cobranças. Marque WhatsApp apenas no número que realmente usa esse canal e confirme o contato com o cliente."
      },
      {
        "titulo": "Documentos cadastrais, quando fizer sentido",
        "corpo": "A empresa pode configurar perfis de documentos para pedir anexos opcionais ou obrigatórios em clientes, fornecedores e pessoas. Isso é opcional e não é tarefa deste onboarding."
      }
    ],
    "acoes": [
      {
        "rotulo": "Abrir cadastro de cliente",
        "rota": "/painel/listagem/clientes/criar",
        "criterio": "cliente_completo"
      }
    ]
  },
  {
    "id": "fornecedores",
    "titulo": "Cadastre quem fornece para a empresa",
    "subtitulo": "Identifique o prestador e mantenha seus contatos acessíveis.",
    "ganho": "Conseguir localizar uma despesa e explicar por que ela foi paga.",
    "contexto": [
      "Tenha em mãos os dados do fornecedor que constam no contrato ou na nota recebida. Confira na listagem se ele já está cadastrado.",
      "Use o fornecedor nas contas a pagar para reunir as obrigações do mesmo prestador. A nota ou fatura pertence à obrigação; o comprovante pertence ao pagamento."
    ],
    "conceitos": [
      {
        "titulo": "Quem recebe",
        "corpo": "Vincular o fornecedor certo evita descrições soltas e facilita consultar todas as contas daquele prestador. Revise identificação e contatos."
      },
      {
        "titulo": "Antes e depois do pagamento",
        "corpo": "A fatura ou documento de origem explica a obrigação. Depois da saída de dinheiro, o comprovante demonstra o pagamento realizado."
      },
      {
        "titulo": "Documentos acessíveis",
        "corpo": "Anexar a documentação no registro correspondente facilita a conferência e a entrega à contabilidade, sem depender de procurar arquivos em conversas."
      }
    ],
    "acoes": [
      {
        "rotulo": "Abrir cadastro de fornecedor",
        "rota": "/painel/listagem/fornecedores/criar",
        "criterio": "fornecedor"
      }
    ]
  },
  {
    "id": "pessoas",
    "titulo": "Cadastre um funcionário da empresa",
    "subtitulo": "Organize a identificação e os contatos de quem faz parte da equipe.",
    "ganho": "Distribuir o trabalho e manter a identificação de quem fez cada ação.",
    "contexto": [
      "Separe nome, identificação e e-mail de quem vai operar o sistema. Confira quais atividades fazem parte da função.",
      "Depois de cadastrar o funcionário, crie um cargo com as permissões necessárias. Em seguida, crie o usuário individual e selecione esse cargo."
    ],
    "conceitos": [
      {
        "titulo": "Pessoa antes de usuário",
        "corpo": "Funcionários, estagiários e sócios representam pessoas da empresa. O usuário é a conta de acesso vinculada à pessoa, com identificação e e-mail conferidos."
      },
      {
        "titulo": "Cargo e permissões",
        "corpo": "O cargo reúne os acessos necessários ao trabalho. Consultar dados, cadastrar, alterar e excluir são responsabilidades diferentes: revise cada permissão."
      },
      {
        "titulo": "Conta individual",
        "corpo": "Evite compartilhar o acesso Master. Contas individuais e o histórico ajudam a identificar o responsável por cada alteração."
      }
    ],
    "acoes": [
      {
        "rotulo": "Cadastrar um funcionário",
        "rota": "/painel/listagem/funcionarios/criar",
        "criterio": "pessoa"
      },
      {
        "rotulo": "Criar cargo e permissões",
        "rota": "/painel/cargos/novo",
        "modulo": Modules.ROLES
      },
      {
        "rotulo": "Criar usuário vinculado",
        "rota": "/painel/listagem/usuarios/criar",
        "criterio": "usuario"
      }
    ]
  },
  {
    "id": "plano",
    "titulo": "Organize as receitas e as despesas",
    "subtitulo": "Comece com um modelo pronto e ajuste quando precisar.",
    "ganho": "Classificar receitas e despesas com consistência desde a primeira conta.",
    "contexto": [
      "Adicione um modelo para começar com contas contábeis, categorias e vínculos preparados.",
      "Prepare a categoria das primeiras receitas: se for emitir notas pelo sistema, complete os campos fiscais no cadastro antes de seguir."
    ],
    "conceitos": [
      {
        "titulo": "Conta contábil e categoria",
        "corpo": "A conta contábil atende à classificação contábil; a categoria organiza a leitura gerencial. O modelo já inclui os vínculos entre elas."
      },
      {
        "titulo": "Estrutura inicial",
        "corpo": "O modelo prepara contas contábeis, categorias e vínculos para o controle financeiro. Na categoria de recebimento, defina o uso de nota fiscal e complete a tributação do serviço quando houver emissão."
      }
    ],
    "acoes": [
      {
        "rotulo": "Ver onde editar as contas contábeis",
        "rota": "/painel/lancamento-contabil"
      },
      {
        "rotulo": "Preparar as categorias para o uso",
        "rota": "/painel/categorias"
      },
      {
        "rotulo": "Ver onde ajustar os vínculos",
        "rota": "/painel/lancamento-contabil/gerenciar",
        "criterio": "plano",
        "rotuloConclusao": "Base com contas contábeis e categorias preparada"
      }
    ]
  },
  {
    "id": "caixas",
    "titulo": "Prepare os caixas da empresa",
    "subtitulo": "Cada conta bancária ou caixa físico precisa ser identificável.",
    "ganho": "Conferir saldo e transferências sem criar receita ou despesa indevida.",
    "contexto": [
      "Separe as contas usadas pela empresa e defina a data de início do controle. O saldo inicial deve corresponder a essa data.",
      "Ao registrar pagamentos e recebimentos, selecione o caixa em que o dinheiro realmente entrou ou saiu."
    ],
    "conceitos": [
      {
        "titulo": "Saldo inicial",
        "corpo": "É o valor conferido na data de início do controle. Na empresa real, use o saldo da conta ou caixa correspondente e evite registrar o mesmo dinheiro duas vezes."
      },
      {
        "titulo": "Transferência interna",
        "corpo": "Mover dinheiro entre caixas muda sua localização, sem alterar o total da empresa. Registrar como receita ou despesa distorceria a análise."
      },
      {
        "titulo": "Conferência e divergência",
        "corpo": "Pesquise os movimentos por período e descrição, confira data, valor e documentação. Investigue duplicidade ou ausência antes de ajustar um saldo."
      }
    ],
    "acoes": [
      {
        "rotulo": "Preparar e consultar caixas",
        "rota": "/painel/financas/caixas/listar",
        "criterio": "caixa"
      }
    ]
  },
  {
    "id": "pagar",
    "titulo": "Registre uma conta a pagar",
    "subtitulo": "Da obrigação ao pagamento, com os documentos de cada momento.",
    "ganho": "Saber o que deve, quando vence e qual saída já aconteceu.",
    "contexto": [
      "Separe o fornecedor, a descrição da despesa, o valor e o vencimento. Tenha a nota ou fatura para conferir a origem.",
      "Cadastre a obrigação primeiro. Registre o pagamento somente quando a saída tiver ocorrido, informando o caixa e anexando o comprovante."
    ],
    "conceitos": [
      {
        "titulo": "Dados da conta",
        "corpo": "Fornecedor identifica quem recebe; conta contábil e categoria explicam a classificação. Valor e vencimento mostram quanto e quando pagar. Centro de custo aparece quando o recurso está ativo."
      },
      {
        "titulo": "Baixa e documentação",
        "corpo": "Na baixa, confira data, valor, forma de pagamento e caixa utilizado. A fatura explica a obrigação; vincule o comprovante ao pagamento realizado."
      },
      {
        "titulo": "Créditos, débitos e recorrência",
        "corpo": "Confira ajustes de crédito ou débito e seus documentos antes de pagar. Despesas recorrentes podem gerar próximas contas, mas gerar uma parcela não significa pagá-la."
      }
    ],
    "acoes": [
      {
        "rotulo": "Cadastrar minha primeira conta a pagar",
        "rota": "/painel/financas/contas-a-pagar",
        "criterio": "conta_pagar"
      },
      {
        "rotulo": "Registrar e conferir um pagamento",
        "rota": "/painel/financas/contas-a-pagar?onboarding=pagamento",
        "criterio": "pagamento"
      }
    ]
  },
  {
    "id": "receber",
    "titulo": "Registre uma conta a receber",
    "subtitulo": "Acompanhe o que foi vendido e o que já entrou no caixa.",
    "ganho": "Conciliar o valor recebido, a pendência do cliente e o saldo do caixa.",
    "contexto": [
      "Separe cliente, descrição do serviço, valor, competência e vencimento. Revise a categoria e a conta contábil.",
      "O recebimento é um registro separado da conta. Confira a entrada real, a data e o caixa antes de dar baixa."
    ],
    "conceitos": [
      {
        "titulo": "Conta e recebimento",
        "corpo": "A conta registra o direito de receber. O recebimento registra uma entrada efetiva, com data, valor, forma e caixa. Emitir boleto, Pix ou link não é uma baixa por si só."
      },
      {
        "titulo": "Parcial ou integral",
        "corpo": "Uma entrada menor que o valor devido mantém saldo pendente. Confira os recebimentos da conta antes de registrar outra baixa para evitar duplicidade."
      },
      {
        "titulo": "Gerenciar recebimentos",
        "corpo": "Abra o recebimento para conferir seus dados e documentos. Revise eventuais créditos e débitos. Na lista, o filtro de situação ajuda a localizar contas já quitadas."
      }
    ],
    "acoes": [
      {
        "rotulo": "Cadastrar minha primeira conta a receber",
        "rota": "/painel/financas/contas-a-receber",
        "criterio": "conta_receber"
      },
      {
        "rotulo": "Registrar e gerenciar recebimentos",
        "rota": "/painel/financas/contas-a-receber?onboarding=recebimento",
        "criterio": "recebimento"
      }
    ]
  },
  {
    "id": "recorrencia",
    "orientacao": true,
    "titulo": "Organize as contas recorrentes",
    "subtitulo": "Conheça as opções para contas que se repetem.",
    "ganho": "Entender como organizar mensalidades e parcelamentos sem repetir o cadastro a cada vencimento.",
    "contexto": [
      "Você já conheceu o cadastro e a baixa de uma conta. Agora, veja como a recorrência organiza contas parceladas ou que se repetem, como mensalidades.",
      "Abra o cadastro para conhecer as opções de recorrência, parcelas e vencimentos. Basta consultar os campos e voltar ao roteiro; não é necessário salvar uma nova conta nesta etapa."
    ],
    "conceitos": [
      {
        "titulo": "O que se repete",
        "corpo": "Única representa uma conta isolada. Parcelado usa uma quantidade definida de parcelas. As demais periodicidades organizam receitas que se repetem. Os campos de dia e data limite dependem da opção escolhida."
      },
      {
        "titulo": "Conta gerada e recebimento",
        "corpo": "A recorrência organiza as próximas contas e seus vencimentos. A baixa continua sendo registrada separadamente, como você viu na etapa anterior."
      },
      {
        "titulo": "Recorrência e automação",
        "corpo": "Recorrência cria contas. A régua de cobrança organiza mensagens e pode ser vinculada à recorrência; os dois recursos se complementam."
      }
    ],
    "acoes": [
      {
        "rotulo": "Conhecer a recorrência",
        "rota": "/painel/financas/contas-a-receber?onboarding=recorrencia"
      }
    ]
  },
  {
    "id": "cobranca",
    "titulo": "Prepare os meios de recebimento",
    "subtitulo": "Configure o meio antes de usá-lo em uma conta.",
    "ganho": "Cobrar com o meio adequado e reconhecer quando o dinheiro foi recebido.",
    "contexto": [
      "Comece pelo credenciamento com os dados da empresa e a conta para repasse. Depois, configure os meios disponíveis para sua empresa.",
      "Com a configuração pronta, emita a cobrança de uma conta a receber em aberto. Se a conta da etapa anterior já foi recebida, lance uma nova conta a receber. O cliente precisa ter endereço e telefone cadastrados para gerar o boleto."
    ],
    "conceitos": [
      {
        "titulo": "Credenciar e ativar",
        "corpo": "Em Recebimentos, credencie a empresa no PJBank e, em Meios de recebimento, ative boleto e Pix pelo botão de cada meio. O cartão de crédito tem credenciamento próprio, com análise documental. Credenciado não significa ativado: confira a situação no botão."
      },
      {
        "titulo": "Boleto, Pix e link",
        "corpo": "São formas de cobrar uma conta. Confira cliente, valor, vencimento e condições antes de emitir ou compartilhar. Acompanhe a situação da cobrança."
      },
      {
        "titulo": "Confirmação antes da baixa",
        "corpo": "Cobrança criada não comprova pagamento. Verifique a confirmação e o recebimento vinculado para evitar dar baixa antecipada ou duplicada."
      }
    ],
    "acoes": [
      {
        "rotulo": "Configurar recebimentos",
        "rota": "/painel/configuracoes/recebimentos",
        "criterio": "recebimentos_configurados"
      },
      {
        "rotulo": "Lançar a conta a receber que será cobrada",
        "rota": "/painel/financas/contas-a-receber"
      },
      {
        "rotulo": "Emitir e acompanhar boleto de uma conta",
        "rota": "/painel/financas/contas-a-receber?onboarding=boleto",
        "criterio": "boleto_emitido"
      }
    ]
  },
  {
    "id": "fiscal",
    "titulo": "Prepare a emissão de notas",
    "subtitulo": "Revise emitente, certificado e classificação do serviço.",
    "ganho": "Identificar pendências antes de solicitar a emissão.",
    "contexto": [
      "Reúna os dados do emitente e o certificado utilizado pela empresa. Revise a classificação do serviço com o responsável fiscal.",
      "Configuração salva, solicitação enviada e nota autorizada são situações distintas. Acompanhe a resposta e trate as pendências indicadas."
    ],
    "conceitos": [
      {
        "titulo": "Emitente e certificado A1",
        "corpo": "Confira a empresa ou filial emissora e a validade do certificado. Ter um arquivo cadastrado não basta se estiver inválido ou vencido. Sem certificado A1, a contratação pode ser feita no próprio sistema."
      },
      {
        "titulo": "Categoria e códigos fiscais",
        "corpo": "Confira a categoria preparada para as primeiras receitas: emissão habilitada, código do serviço, NBS apresentada e tributação municipal revisados com a contabilidade. A nota usa a categoria vinculada à conta a receber."
      },
      {
        "titulo": "Pendência e autorização",
        "corpo": "Solicitação enviada não equivale a nota emitida. Após solicitar a emissão, confira a autorização e os erros informados e trate as pendências indicadas."
      }
    ],
    "acoes": [
      {
        "rotulo": "Preparar configuração fiscal",
        "rota": "/painel/configuracoes/fiscal-rps",
        "criterio": "fiscal_configurado"
      },
      {
        "rotulo": "Revisar tributação das categorias",
        "rota": "/painel/categorias?onboarding=tributacao"
      },
      {
        "rotulo": "Emitir nota da conta a receber",
        "rota": "/painel/financas/contas-a-receber?onboarding=nota",
        "criterio": "nota_emitida"
      },
      {
        "rotulo": "Conferir situação das notas",
        "rota": "/painel/fiscal/notas"
      }
    ]
  },
  {
    "id": "automacao",
    "titulo": "Organize os lembretes de cobrança",
    "subtitulo": "Mensagem, destinatário e prazo conforme a sua rotina.",
    "ganho": "Organizar lembretes sem depender de lembrar cada envio manualmente.",
    "contexto": [
      "Revise a apresentação e o texto das mensagens antes de configurar as etapas da régua. Defina os prazos a partir da política de cobrança da empresa.",
      "Confira os contatos e os vínculos antes de ativar. Uma régua ativa pode enviar mensagens reais quando as condições forem atendidas."
    ],
    "conceitos": [
      {
        "titulo": "Layout e template",
        "corpo": "O layout organiza a apresentação; o template define a mensagem. Confira identificação da empresa, dados da cobrança e clareza do texto em cada canal."
      },
      {
        "titulo": "Régua e vínculo",
        "corpo": "As etapas definem quando cobrar em relação ao vencimento. Uma régua precisa estar ativa e vinculada à conta ou recorrência para participar da operação."
      },
      {
        "titulo": "Cópia e acompanhamento",
        "corpo": "Revise quem recebe a cópia da cobrança. Confira o histórico de envios e as notificações para identificar falhas ou contatos que precisam de correção."
      }
    ],
    "acoes": [
      {
        "rotulo": "Revisar layout da cobrança",
        "rota": "/painel/configuracoes/cobranca/layout"
      },
      {
        "rotulo": "Preparar templates",
        "rota": "/painel/configuracoes/cobranca/templates"
      },
      {
        "rotulo": "Criar e ativar régua de cobrança",
        "rota": "/painel/configuracoes/cobranca/fluxo"
      },
      {
        "rotulo": "Revisar cópia da cobrança",
        "rota": "/painel/configuracoes/cobranca/copia"
      },
      {
        "rotulo": "Vincular régua à conta",
        "rota": "/painel/financas/contas-a-receber?onboarding=automacao",
        "criterio": "automacao"
      }
    ]
  },
  {
    "id": "relatorios",
    "titulo": "Prepare a informação financeira",
    "subtitulo": "Escolha o relatório pelo que você precisa acompanhar.",
    "ganho": "Gerar uma visão financeira e preparar a informação para a contabilidade.",
    "contexto": [
      "Defina o período e a finalidade antes de gerar o relatório. Confira os filtros e acompanhe o processamento até o arquivo ficar disponível.",
      "Para a contabilidade, revise a classificação e reúna os documentos que sustentam os lançamentos."
    ],
    "conceitos": [
      {
        "titulo": "Relatório e finalidade",
        "corpo": "Fluxo de caixa ajuda a acompanhar entradas, saídas e disponibilidade. Explore as modalidades disponíveis conforme a análise desejada e confira se o período escolhido corresponde à pergunta."
      },
      {
        "titulo": "Solicitar e concluir",
        "corpo": "Uma solicitação enfileirada ou em processamento ainda não é um relatório pronto. Acompanhe a situação e baixe o arquivo quando a geração terminar."
      },
      {
        "titulo": "Planilha e documentação contábil",
        "corpo": "Confira conta contábil, categoria, datas e valores. Faturas e comprovantes de créditos e débitos permitem entender a origem e a liquidação, melhorando agilidade, qualidade e segurança da conferência."
      }
    ],
    "acoes": [
      {
        "rotulo": "Gerar relatório financeiro",
        "rota": "/painel/financas/relatorios",
        "criterio": "relatorio_gerado"
      },
      {
        "rotulo": "Gerar planilha contábil",
        "rota": "/painel/financas/planilha-contabil",
        "criterio": "planilha_gerada"
      },
      {
        "rotulo": "Conferir documentos financeiros",
        "rota": "/painel/financas/documentos",
        "modulo": Modules.ACCOUNTING_DOCUMENTS
      }
    ]
  },
  {
    "id": "autonomia",
    "orientacao": true,
    "titulo": "Continue a rotina com apoio",
    "subtitulo": "Ajuda, histórico e serviços da empresa em um só roteiro.",
    "ganho": "Resolver dúvidas e acompanhar serviços sem depender de uma implantação por reunião.",
    "contexto": [
      "Consulte a ajuda ao precisar de um procedimento e o suporte ao encontrar um impedimento. Informe a tela e a mensagem apresentada.",
      "Acompanhe créditos e consumo antes de contratar serviços adicionais. Use o histórico para entender alterações nos registros."
    ],
    "conceitos": [
      {
        "titulo": "Base de conhecimento e suporte",
        "corpo": "Use a base de conhecimento no menu de ajuda para aprender os procedimentos. Para um impedimento, abra um chamado com contexto, etapa e mensagem de erro, evitando enviar senhas ou segredos."
      },
      {
        "titulo": "Notificações e histórico",
        "corpo": "Notificações ajudam a acompanhar acontecimentos que pedem atenção. O histórico permite conferir alterações e responsáveis; consulte o registro relacionado antes de repetir uma ação."
      },
      {
        "titulo": "Créditos e serviços adicionais",
        "corpo": "Consulte saldo, consumo, condições e valores apresentados no sistema antes de recarregar ou contratar um serviço cobrado à parte. Este guia não fixa preços nem inclui uma compra."
      }
    ],
    "acoes": [
      {
        "rotulo": "Consultar créditos e consumo",
        "rota": "/painel/creditos"
      },
      {
        "rotulo": "Acompanhar chamados de suporte",
        "rota": "/painel/suporte"
      },
      {
        "rotulo": "Consultar histórico de ações",
        "rota": "/painel/historicos/acoes"
      }
    ]
  }
];

export const FASES_ONBOARDING = [
  {
    "id": "base",
    "titulo": "Base confiável",
    "curto": "Organizar",
    "icone": "layers",
    "descricao": "Conecte pessoas, contatos e classificações para começar com clareza.",
    "capacidade": "Dados organizados para sustentar a operação.",
    "capitulos": [
      "fundamentos",
      "clientes",
      "fornecedores",
      "pessoas",
      "plano"
    ]
  },
  {
    "id": "movimento",
    "titulo": "Caixa em movimento",
    "curto": "Movimentar",
    "icone": "sync_alt",
    "descricao": "Acompanhe o que deve, o que recebe e o dinheiro que realmente mudou de lugar.",
    "capacidade": "Contas e caixa contando a mesma história.",
    "capitulos": [
      "caixas",
      "pagar",
      "receber",
      "recorrencia"
    ]
  },
  {
    "id": "controle",
    "titulo": "Cobrança e controle",
    "curto": "Cobrar e emitir",
    "icone": "fact_check",
    "descricao": "Prepare cobranças, documentos e automações com os dados certos.",
    "capacidade": "Cobranças e documentos com contexto conferido.",
    "capitulos": [
      "cobranca",
      "fiscal",
      "automacao"
    ]
  },
  {
    "id": "autonomia",
    "titulo": "Autonomia para decidir",
    "curto": "Acompanhar",
    "icone": "bar_chart",
    "descricao": "Encontre registros, antecipe riscos e transforme informação em decisão.",
    "capacidade": "Informação confiável para conduzir a rotina.",
    "capitulos": [
      "relatorios",
      "autonomia"
    ]
  }
];
